import { useState, useEffect } from 'react';
import { axiosInstance } from "../api/api";
import { HistoryType, SpotDetailType } from '../types/HistoryType';
import { usePermission } from '../hooks/usePermission';

export const useGetHistory = (params?: {
  filter?: string;
  start_at?: string;
  end_at?: string;
  sort_by?: string;
  sort_type?: 'asc' | 'desc';
  per_page?: number;
}) => {
  const [history, setHistory] = useState<HistoryType[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const { canIndex: canAccessRoutes } = usePermission('patrol_user_route_index');
  const { canIndex: canAccessComponents } = usePermission('patrol_user_route_component_index');

  const getDayName = (dateString: string): string => {
    const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const date = new Date(dateString);
    return days[date.getDay()];
  };
  
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dayName = getDayName(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${dayName}, ${day}-${month}-${year}`;
  };

  const formatTime = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const extractTimeOnly = (timeString: string): string => {
    if (!timeString) return '';
    const parts = timeString.split(/\s+/);
    const timePart = parts.find(part => part.includes(':'));
    
    if (timePart) {
      const timeMatch = timePart.match(/(\d{1,2}:\d{2})/);
      if (timeMatch) {
        return timeMatch[1];
      }
    }
    return timeString;
  };

  const getPatrolInTime = (availablePatrols: any[], patrolId: string): string => {
    if (!patrolId || patrolId === 'unknown' || !availablePatrols.length) return '';
    
    const matchingPatrol = availablePatrols.find((p: any) => p.id === patrolId);
    if (matchingPatrol && matchingPatrol.in) {
      return extractTimeOnly(matchingPatrol.in);
    }
    return '';
  };

  const getScannedBy = (component: any): string => {
    if (component.created_by?.employee?.employee_personal?.full_name) {
      return `${component.created_by.employee.employee_personal.full_name} (${component.created_by.employee.employee_personal.employee_id})`;
    } else if (component.created_by?.employee?.employee_personal?.first_name) {
      return `${component.created_by.employee.employee_personal.first_name} (${component.created_by.employee.employee_personal.employee_id})`;
    } else if (component.created_by?.user?.name) {
      return `${component.created_by.user.name} (${component.created_by.user_id})`;
    }
    return 'Unknown User';
  };

  const getHistory = async () => {
    setHistoryLoading(true);
    try {
      if (!canAccessRoutes) {
        console.warn("User doesn't have permission to access patrol routes");
        setHistory([]);
        return;
      }

      const patrolGroupId = localStorage.getItem("patrol_group_id");
      const paramsToSend = {
        ...params,
        ...(patrolGroupId && { patrol_group_id: patrolGroupId })
      };

      const historyRes = await axiosInstance.get(
        "/api/v1/patrol/user/route/index",
        { params: paramsToSend }
      );

      const apiData = historyRes.data.data || [];
      
      const mappedHistory = await Promise.all(
        apiData.map(async (routeItem: any) => {
          try {
            if (!canAccessComponents) {
              console.warn("User doesn't have permission to access route components");
              const createdAt = routeItem.created_at || '';
              return {
                id: routeItem.id,
                patrolName: routeItem.group?.name || 'Unknown Patrol',
                date: formatDate(createdAt),
                day: getDayName(createdAt),
                clockIn: formatTime(createdAt),
                groupName: routeItem.group?.name || 'Unknown Group',
                spotDetails: [],
                totalSpots: 0,
                completedSpots: 0,
                status: 'pending',
                patrol_route_id: routeItem.id,
                rawDate: createdAt ? createdAt.split('T')[0] : '',
                noPermission: true,
                hasMoreSpots: false
              } as HistoryType;
            }

            const detailRes = await axiosInstance.get(
              `/api/v1/patrol/user/route/component/${routeItem.id}/index`,
              { params: { page: 1, per_page: 5 } }
            );

            const components = detailRes.data.data || [];
            const totalSpots = detailRes.data.meta?.total || detailRes.data.total || 0;
            const hasMoreSpots = totalSpots > 5;
            const componentsByPatrol = components.reduce((acc: any, component: any) => {
              const patrolId = component.component?.patrol_id || 'unknown';
              if (!acc[patrolId]) {
                acc[patrolId] = [];
              }
              acc[patrolId].push(component);
              return acc;
            }, {});

            const patrolIds = Object.keys(componentsByPatrol);
            const availablePatrols = routeItem.group?.patrols || [];
            
            if (patrolIds.length > 1) {
              const multipleHistoryItems = patrolIds.map((patrolId) => {
                const patrolComponents = componentsByPatrol[patrolId];
                
                const spotDetails = patrolComponents.map((component: any) => ({
                  id: component.id,
                  patrolId: patrolId,
                  spotName: component.component?.asset?.name || 'Unknown Spot',
                  scannedTime: formatTime(component.created_at || ''),
                  notes: component.description || '-',
                  photos: component.files?.map((f: any) => f.url) || [],
                  scannedBy: getScannedBy(component),
                  status: 'scanned'
                }));

                let patrolName = 'Unknown Patrol';
                
                if (patrolId !== 'unknown') {
                  const matchingPatrol = availablePatrols.find((p: any) => p.id === patrolId);
                  if (matchingPatrol) {
                    patrolName = matchingPatrol.name;
                  }
                }
                
                if (patrolName === 'Unknown Patrol' && availablePatrols.length > 0) {
                  patrolName = availablePatrols[0].name;
                }

                const createdAt = routeItem.created_at || '';
                const clockIn = getPatrolInTime(availablePatrols, patrolId);

                return {
                  id: `${routeItem.id}-${patrolId}`,
                  patrolName: patrolName,
                  date: formatDate(createdAt),
                  day: getDayName(createdAt),
                  clockIn: clockIn,
                  groupName: routeItem.group?.name || 'Unknown Group',
                  spotDetails: spotDetails,
                  totalSpots: totalSpots,
                  completedSpots: patrolComponents.length,
                  status: routeItem.is_finished ? 'done' : 'pending',
                  patrol_route_id: routeItem.id,
                  rawDate: createdAt ? createdAt.split('T')[0] : '',
                  hasMoreSpots: hasMoreSpots
                } as HistoryType;
              });
              
              return multipleHistoryItems;
            } else {
              const patrolComponents = componentsByPatrol[patrolIds[0]] || [];
              
              const spotDetails = patrolComponents.map((component: any) => ({
                id: component.id,
                patrolId: patrolIds[0],
                spotName: component.component?.asset?.name || 'Unknown Spot',
                scannedTime: formatTime(component.created_at || ''),
                notes: component.description || '-',
                photos: component.files?.map((f: any) => f.url) || [],
                scannedBy: getScannedBy(component),
                status: 'scanned'
              }));

              let patrolName = 'Unknown Patrol';
              const patrolId = patrolIds[0];
              
              if (patrolId !== 'unknown' && availablePatrols.length > 0) {
                const matchingPatrol = availablePatrols.find((p: any) => p.id === patrolId);
                if (matchingPatrol) {
                  patrolName = matchingPatrol.name;
                }
              }
              
              if (patrolName === 'Unknown Patrol' && availablePatrols.length > 0) {
                patrolName = availablePatrols[0].name;
              }

              const createdAt = routeItem.created_at || '';
              const clockIn = getPatrolInTime(availablePatrols, patrolId);

              return {
                id: routeItem.id,
                patrolName: patrolName,
                date: formatDate(createdAt),
                day: getDayName(createdAt),
                clockIn: clockIn,
                groupName: routeItem.group?.name || 'Unknown Group',
                spotDetails: spotDetails,
                totalSpots: totalSpots,
                completedSpots: patrolComponents.length, 
                status: routeItem.is_finished ? 'done' : 'pending',
                patrol_route_id: routeItem.id,
                rawDate: createdAt ? createdAt.split('T')[0] : '',
                hasMoreSpots: hasMoreSpots 
              } as HistoryType;
            }

          } catch (error) {
            console.warn(`Failed to fetch detail for route ${routeItem.id}:`, error);
            
            const createdAt = routeItem.created_at || '';
            return {
              id: routeItem.id,
              patrolName: routeItem.group?.name || 'Unknown Patrol',
              date: formatDate(createdAt),
              day: getDayName(createdAt),
              clockIn: formatTime(createdAt),
              groupName: routeItem.group?.name || 'Unknown Group',
              spotDetails: [],
              totalSpots: 0,
              completedSpots: 0,
              status: 'pending',
              patrol_route_id: routeItem.id,
              rawDate: createdAt ? createdAt.split('T')[0] : '',
              hasMoreSpots: false 
            } as HistoryType;
          }
        })
      );

      const flattenedHistory = mappedHistory.flat();
      
      setHistory(flattenedHistory);
    } catch (err) {
      console.error("Error fetching history:", err);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    getHistory();
  }, [JSON.stringify(params), canAccessRoutes, canAccessComponents]);
  return { historyLoading, history, refetch: getHistory };
};